import useDocumentTitle from 'hooks/useDocumentTitle'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import { Container } from 'reactstrap'
import { fetchAllCampaignsThunk } from 'slices/campaigns/thunk'
import { AppDispatch, RootState } from 'store'
import { Campaign } from 'types/campaign'
import platformInstagram from 'assets/icons/instagram-logo.svg'
import platformYoutube from 'assets/icons/youtube-logo.svg'
import './style.scss'
import { formatNumber, toValidNumber } from 'utils/helpers'

const platformBadgeSrc = (platform?: string) => {
    if (platform === 'ig' || platform === 'instagram') return platformInstagram
    if (platform === 'yt' || platform === 'youtube') return platformYoutube
    return null
}

const formatTierLabel = (bucket?: string) => {
    if (!bucket) return '—'
    const trimmed = bucket.trim()
    if (!trimmed) return '—'
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

const CampaignCard: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
    const platformImg = platformBadgeSrc(campaign.platform)
    const seats = campaign.requirements?.creator_count?.max
    const seatsLabel = seats != null && seats > 0 ? `${seats} seats` : '—'

    return (
        <Link to={`/campaign/${campaign.uuid}`} className="campaign-card-link">
            <article className="campaign-card">
                <div className="campaign-card__header">
                    {campaign.hero_img_url ? (
                        <img
                            src={campaign.hero_img_url}
                            alt=""
                            className="campaign-card__hero"
                        />
                    ) : (
                        <div className="campaign-card__hero" aria-hidden />
                    )}
                    {platformImg && (
                        <div className="campaign-card__platform-badge">
                            <img src={platformImg} alt="" />
                        </div>
                    )}
                </div>
                <div className="campaign-card__body">
                    <h4 className="campaign-card__title">{campaign.name}</h4>
                    <p className="campaign-card__subtitle">
                        {campaign.brand?.category || campaign.brand?.name || ''}
                    </p>
                    <div className="campaign-card__footer-pill">
                        <span className={`${toValidNumber(campaign.budget) > 0 ? 'text-success' : ''}`}>{toValidNumber(campaign.budget) > 0 ? `₹${formatNumber(toValidNumber(campaign.budget ?? 0))}` : 'Barter'}</span>
                        <span className="campaign-card__tier">
                            <i className="ri-user-heart-line fs-16 text-secondary" aria-hidden />
                            {formatTierLabel(campaign.requirements?.creator_bucket)}
                        </span>
                        <span>{seatsLabel}</span>
                    </div>
                </div>
            </article>
        </Link>
    )
}

const Campaigns = () => {
    useDocumentTitle('Campaigns')

    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId)
    const dispatch: AppDispatch = useDispatch()
    const { all_campaigns, loading } = useSelector((state: RootState) => state.campaigns)
    const [searchQuery, setSearchQuery] = useState('')
    useEffect(() => {
        if (selectedHubId) {
            dispatch(fetchAllCampaignsThunk({}))
        }
    }, [selectedHubId, dispatch])

    const filteredCampaigns = useMemo(() => {
        return all_campaigns.filter((campaign) => campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) || campaign.brand?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || campaign.brand?.category?.toLowerCase().includes(searchQuery.toLowerCase()))
    }, [all_campaigns, searchQuery])

    const showSkeleton = loading && all_campaigns.length === 0

    return (
        <div className="page-content campaigns-page mb-3">
            <Container fluid>
                <div className="d-flex align-items-md-center justify-content-between flex-md-row flex-column mb-4 gap-2">
                    <h3 className="page-title m-0">Campaigns</h3>
                    <div className="d-flex gap-2">
                        <div className="campaign-layout-search-container position-relative">
                            <span>
                                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            <input type="text" placeholder='Search...' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="campaign-layout-search-input" />
                            {
                                searchQuery &&
                                <i className="ri-close-line clear-search-icon" onClick={() => setSearchQuery('')}></i>
                            }
                        </div>
                    </div>
                </div>

                <div className="campaign-cards-grid">
                    {showSkeleton &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="campaign-card">
                                <Skeleton height={200} className="w-100" style={{ borderRadius: 0 }} />
                                <div className="p-3">
                                    <Skeleton height={22} className="mb-2" />
                                    <Skeleton height={18} width="60%" className="mb-3" />
                                    <Skeleton height={40} borderRadius={999} />
                                </div>
                            </div>
                        ))}
                    {!showSkeleton &&
                        filteredCampaigns.map((campaign) => (
                            <CampaignCard key={campaign.uuid} campaign={campaign} />
                        ))}
                </div>

                {!loading && !showSkeleton && filteredCampaigns.length === 0 && (
                    <div className="d-flex flex-column gap-2 align-items-center text-center py-5 mb-0">
                        <span className='fw-bold text-muted fs-20'>No campaigns found.</span>
                        {
                            searchQuery &&
                            <span className='text-muted'>Try searching for a different keyword.</span>
                        }
                    </div>
                )}
            </Container>
        </div>
    )
}

export default Campaigns
